import { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, Underline, Strikethrough, List, ListOrdered, Quote, Heading2, Heading3, RemoveFormatting } from "lucide-react";

const exec = (cmd, val = null) => document.execCommand(cmd, false, val);

export function RichText({ value, onChange, id = "field" }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) ref.current.innerHTML = value || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sync = () => onChange(ref.current.innerHTML);
  const run = (cmd, val) => (e) => { e.preventDefault(); ref.current.focus(); exec(cmd, val); sync(); };

  const Btn = ({ cmd, val, icon: Icon, label }) => (
    <button type="button" title={label} aria-label={label} onMouseDown={run(cmd, val)}
      className="w-9 h-9 grid place-items-center rounded-lg text-slate-600 hover:bg-white hover:text-blue-600 active:scale-90 transition" data-testid={`richtext-${id}-${cmd}${val ? "-" + val : ""}`}>
      <Icon className="w-4 h-4" />
    </button>
  );
  const Size = ({ s, label }) => (
    <button type="button" title={`Ukuran ${label}`} onMouseDown={run("fontSize", s)}
      className="h-9 px-2 rounded-lg text-slate-600 hover:bg-white hover:text-blue-600 active:scale-90 transition font-bold" data-testid={`richtext-${id}-size-${s}`}>{label}</button>
  );

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-white focus-within:ring-2 focus-within:ring-blue-500/40" data-testid={`richtext-${id}`}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-100 bg-slate-50 px-2 py-1.5">
        <Btn cmd="bold" icon={Bold} label="Tebal" />
        <Btn cmd="italic" icon={Italic} label="Miring" />
        <Btn cmd="underline" icon={Underline} label="Garis bawah" />
        <Btn cmd="strikeThrough" icon={Strikethrough} label="Coret" />
        <span className="w-px h-6 bg-slate-200 mx-1" />
        <span className="flex items-center gap-0.5">
          <Size s="2" label="A-" />
          <Size s="3" label="A" />
          <Size s="5" label="A+" />
          <Size s="6" label="A++" />
        </span>
        <span className="w-px h-6 bg-slate-200 mx-1" />
        <Btn cmd="formatBlock" val="H2" icon={Heading2} label="Judul" />
        <Btn cmd="formatBlock" val="H3" icon={Heading3} label="Subjudul" />
        <Btn cmd="insertUnorderedList" icon={List} label="Daftar poin" />
        <Btn cmd="insertOrderedList" icon={ListOrdered} label="Daftar nomor" />
        <Btn cmd="formatBlock" val="BLOCKQUOTE" icon={Quote} label="Kutipan" />
        <span className="w-px h-6 bg-slate-200 mx-1" />
        <Btn cmd="removeFormat" icon={RemoveFormatting} label="Hapus format" />
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning onInput={sync} onBlur={sync}
        data-placeholder="Tulis isi renungan di sini..."
        className="rich-content min-h-[200px] max-h-[380px] overflow-y-auto px-4 py-3 text-sm text-slate-700 leading-relaxed focus:outline-none"
        data-testid={`richtext-${id}-area`} />
    </div>
  );
}

export function RichContent({ html, className = "" }) {
  const raw = html || "";
  const isHtml = /<[a-z][\s\S]*>/i.test(raw);
  if (!isHtml) return <p className={`whitespace-pre-line ${className}`}>{raw}</p>;
  const clean = DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } });
  return <div className={`rich-content ${className}`} dangerouslySetInnerHTML={{ __html: clean }} />;
}
